import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../auth/authContext.tsx";
import { useToast } from "../../hooks/useToast.ts";
import { saveAppConfig } from "../../api/endpoints.ts";
import { appRegistry } from "./appRegistry.ts";
import styles from "./classicDashboard.module.scss";

interface ConfigureDialogProps {
  onClose(): void;
}

interface AppEntry {
  name: string;
  title: string;
}

export function ConfigureDialog({ onClose }: ConfigureDialogProps) {
  const { cache, refreshCache, canRead } = useAuth();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [columns, setColumns] = useState(() => {
    const raw = (cache?.UserApps ?? []) as { Col?: number; [k: string]: unknown }[];
    const maxCol = raw.reduce((m, a) => Math.max(m, a.Col ?? 0), 0);
    return raw.length > 0 ? Math.max(1, maxCol + 1) : 3;
  });
  const dialogRef = useRef<HTMLDivElement>(null);

  // Current configured apps (from UserApps)
  const [myApps, setMyApps] = useState<AppEntry[]>(() => {
    const raw = (cache?.UserApps ?? []) as { AppName?: string; [k: string]: unknown }[];
    return raw
      .filter((a) => a.AppName && appRegistry[a.AppName])
      .map((a) => ({
        name: a.AppName!,
        title: appRegistry[a.AppName!]?.title ?? a.AppName!,
      }));
  });

  // Available apps from page cache (Type === "APP", Enabled, Available, with permission)
  const availableApps = useMemo<AppEntry[]>(() => {
    const pages = cache?.PageInfo?.Page ?? [];
    const configured = new Set(myApps.map((a) => a.name));

    // Get APP-type pages the user has access to
    const appPages = pages.filter(
      (p) => p.Type === "APP" && p.Enabled && p.Available && canRead(p.RowID)
    );

    // Map page Name to registry entries; page Name may match registry key
    const available: AppEntry[] = [];
    for (const p of appPages) {
      if (!configured.has(p.Name) && appRegistry[p.Name]) {
        available.push({ name: p.Name, title: appRegistry[p.Name]!.title });
      }
    }

    // Also include any registry entry not covered by page cache
    // (some apps may not have page entries but are in the registry)
    for (const key of Object.keys(appRegistry)) {
      if (!configured.has(key) && !available.some((a) => a.name === key)) {
        // Only add if it doesn't require a specific page permission
        const inPages = pages.find((p) => p.Name === key);
        if (!inPages || (inPages.Enabled && inPages.Available && canRead(inPages.RowID))) {
          available.push({ name: key, title: appRegistry[key]!.title });
        }
      }
    }

    return available.sort((a, b) => a.title.localeCompare(b.title));
  }, [cache, myApps, canRead]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  // Close on overlay click
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  const addApp = useCallback((app: AppEntry) => {
    setMyApps((prev) => [...prev, app]);
  }, []);

  const removeApp = useCallback((index: number) => {
    setMyApps((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const moveApp = useCallback((index: number, direction: -1 | 1) => {
    setMyApps((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target]!, next[index]!];
      return next;
    });
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const appNames = myApps.map((a) => a.name);
      const result = await saveAppConfig(appNames, columns);
      if (result.bError) {
        toast(result.sMessage ?? "Failed to save configuration", "error");
        return;
      }
      // Refresh the cache to pick up new UserApps
      await refreshCache();
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  }, [myApps, columns, refreshCache, onClose]);

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.dialog} ref={dialogRef} role="dialog" aria-label="Configure Dashboard">
        <div className={styles.dialogHeader}>
          Configure Dashboard Apps
          <button className={styles.dialogClose} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className={styles.dialogBody}>
          <div className={styles.columnControl}>
            <label htmlFor="col-count">Columns:</label>
            <input
              id="col-count"
              type="number"
              min={1}
              max={5}
              value={columns}
              onChange={(e) => setColumns(Math.max(1, Math.min(5, Number(e.target.value) || 1)))}
            />
          </div>

          <div className={styles.configPanels}>
            {/* Left: configured apps */}
            <div className={styles.configPanel}>
              <div className={styles.configPanelTitle}>
                My Apps ({myApps.length})
              </div>
              <div className={styles.configList}>
                {myApps.length === 0 && (
                  <span style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", padding: "var(--space-2)" }}>
                    No apps configured. Add apps from the right panel.
                  </span>
                )}
                {myApps.map((app, i) => (
                  <div key={`${app.name}-${i}`} className={styles.configItem}>
                    <span className={styles.configItemOrder}>{i + 1}</span>
                    <span className={styles.configItemName}>{app.title}</span>
                    <div className={styles.configItemActions}>
                      <button
                        className={styles.configIconBtn}
                        onClick={() => moveApp(i, -1)}
                        disabled={i === 0}
                        title="Move up"
                      >
                        ▲
                      </button>
                      <button
                        className={styles.configIconBtn}
                        onClick={() => moveApp(i, 1)}
                        disabled={i === myApps.length - 1}
                        title="Move down"
                      >
                        ▼
                      </button>
                      <button
                        className={styles.configIconBtn}
                        onClick={() => removeApp(i)}
                        title="Remove"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: available apps to add */}
            <div className={styles.configPanel}>
              <div className={styles.configPanelTitle}>
                Available Apps ({availableApps.length})
              </div>
              <div className={styles.configList}>
                {availableApps.length === 0 && (
                  <span style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", padding: "var(--space-2)" }}>
                    All available apps are already added.
                  </span>
                )}
                {availableApps.map((app) => (
                  <div key={app.name} className={styles.configItem}>
                    <span className={styles.configItemName}>{app.title}</span>
                    <button
                      className={styles.configIconBtn}
                      onClick={() => addApp(app)}
                      title="Add to my apps"
                    >
                      +
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.dialogFooter}>
          <button className={styles.btnSecondary} onClick={onClose}>
            Cancel
          </button>
          <button className={styles.btnPrimary} onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
